import React from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";
import Article from "./Article";
import { formatForDisplay } from './utils';

function Clients({ statsData, onViewDetails }) {
    const [clients, setClients] = React.useState([]);
    const [lastId, setLastId] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [hasMore, setHasMore] = React.useState(true);
    const observer = React.useRef();
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();

    const location = useLocation();
    const navigate = useNavigate();

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        try {
            const url = `/clients?limit=10${lastId ? `&last_id=${lastId}` : ""}`;
            const response = await axiosPrivate.get(url, {headers: {Authorization: "Bearer " + auth.accessToken}});
            setClients(prevItems => [...prevItems, ...response.data]);
            setHasMore(response.data.length > 0);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [axiosPrivate, auth.accessToken, lastId]);

    React.useEffect(() => {
        setClients([]);
        setLastId(null);
    }, [location]);

    React.useEffect(() => {
        fetchData();
    }, [fetchData]);

    const lastItemRef = React.useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setLastId(node.id);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const selectedArticle = statsData.find(
        article => article.link === location.pathname
    );

    const selectedArticleRef = React.useRef(null);

    React.useEffect(() => {
        if (selectedArticleRef.current) {
            selectedArticleRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, []);

    const downloadExcel = async () => {
        console.log("downloadExcel");
        try {
            const queryParams = new URLSearchParams();
            if (lastId) queryParams.append("last_id", lastId);
            const url = `/clients_csv?${queryParams.toString()}`;
            const response = await axiosPrivate.get(url, {headers: {Authorization: "Bearer " + auth.accessToken}});
            const href = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", 'clients.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch(err) {
            console.log(err);
        }
    };

    if (!selectedArticle) {
        return <div>Article not found</div>;
    }

    return (
        <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/4">
                <div className="flex overflow-x-auto md:overflow-x-hidden md:overflow-y-auto md:max-h-screen md:h-screen md:pb-32 md:pr-4 md:grid md:grid-cols-1 gap-6 pb-4 md:pb-0">
                    {statsData.map((article, index) => (
                        <Article
                            key={index}
                            title={article.title}
                            value={article.value}
                            isObject={article.isObject}
                            isSelected={selectedArticle.title === article.title}
                            onViewDetails={() => onViewDetails(article)}
                            ref={selectedArticle.title === article.title ? selectedArticleRef : null}
                        />
                    ))}
                </div>
            </div>
            <div className="w-full md:w-3/4">
                <div className="rounded-lg border border-gray-200 bg-slate-100 p-6 sticky top-6">
                    <h2 className="text-2xl font-medium text-gray-900">{selectedArticle.title}</h2>
                    <p className="text-sm text-gray-500">Details for {selectedArticle.title}</p>
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                            <div>
                                <h3 className="text-lg font-medium text-gray-800">Registered Clients</h3>
                                <p className="text-sm text-gray-500">
                                    {clients.length} clients{clients.length !== 1 ? 's' : ''}
                                </p>
                            </div>
                            {clients && clients.length > 0 && (
                                <button 
                                    onClick={downloadExcel}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                >
                                    Download Excel
                                </button>
                            )}
                        </div>

                        {clients && clients.length > 0 ? (
                            <div className="overflow-hidden">
                                <div className="max-h-80 overflow-y-auto">
                                    <table className="min-w-full">
                                        <thead className="bg-gray-50 sticky top-0 z-10">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    #
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Display Name
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Mobile Number
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Id Number
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Musoni System ID
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {clients.map((item, index) => {
                                                if (clients.length === index + 1) {
                                                    return (
                                                        <tr id={item.id} ref={lastItemRef} key={index} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {index + 1}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {item.display_name}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {formatForDisplay(item.mobile_num)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {item.id_num}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {item.musoni_system_id}
                                                            </td>
                                                        </tr>
                                                    )
                                                } else {
                                                    return (
                                                        <tr id={item.id} key={index} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {index + 1}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {item.display_name}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {formatForDisplay(item.mobile_num)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {item.id_num}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {item.musoni_system_id}
                                                            </td>
                                                        </tr>
                                                    )
                                                }
                                            })}
                                        </tbody>
                                    </table>
                                    {loading && <div>Loading...</div>}
                                    {!hasMore && <div>No more items to load</div>}
                                </div>
                            </div>
                            ) : (
                            <div className="text-center py-8 text-gray-500">No clients found</div>
                        )}
                    </div>
                    <button onClick={() => navigate("/dashboard")} className="mt-4 inline-block text-blue-600 hover:underline">
                        ← Back to List
                    </button>
                </div>
            </div>
        </div>
    )
}

export default Clients;